import React, { useEffect, useState } from "react";
import { motion } from "motion/react";
import {
    FiFileText,
    FiCheckCircle,
    FiInfo,
    FiUserCheck,
    FiAward,
    FiUsers,
    FiAlertTriangle,
    FiShieldOff,
    FiRefreshCw,
    FiGlobe,
    FiMail,
    FiArrowRight,
    FiArrowUp,
} from "react-icons/fi";
import { HiSparkles } from "react-icons/hi2";
import ClientNavbar from "../components/Navbar/ClinetNavbar";
import FixedButton from "../components/Client/FixedButton";
import ClientFooter from "../components/Client/ClientFooter";
import ClientAdSense from "../components/Client/ClientAdSense";

const SECTIONS = [
    { id: "accept", label: "Acceptance", icon: FiCheckCircle },
    { id: "service", label: "Description of Service", icon: FiInfo },
    { id: "responsibilities", label: "User Responsibilities", icon: FiUserCheck },
    { id: "ip", label: "Intellectual Property", icon: FiAward },
    { id: "thirdparty", label: "Third-Party & Ads", icon: FiUsers },
    { id: "warranty", label: "No Warranty", icon: FiAlertTriangle },
    { id: "liability", label: "Limitation of Liability", icon: FiShieldOff },
    { id: "modifications", label: "Modifications", icon: FiRefreshCw },
    { id: "law", label: "Governing Law", icon: FiGlobe },
    { id: "contact", label: "Contact Us", icon: FiMail },
];

const fadeUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.15 },
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
};

const SectionShell = ({ id, num, icon: Icon, title, children }) => (
    <motion.section
        id={id}
        {...fadeUp}
        className='ios-glass-dark relative rounded-3xl p-5 md:p-8 overflow-hidden scroll-mt-28'
        style={{
            boxShadow:
                "0 14px 36px -14px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.1)",
        }}
    >
        <div
            aria-hidden
            className='absolute -top-16 -right-16 w-56 h-56 rounded-full opacity-15 blur-3xl pointer-events-none'
            style={{
                background:
                    "radial-gradient(circle, #7F00FF 0%, transparent 70%)",
            }}
        />
        <div className='relative flex items-center gap-3 mb-5'>
            <div
                className='w-11 h-11 md:w-12 md:h-12 rounded-2xl flex items-center justify-center flex-shrink-0'
                style={{
                    background:
                        "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                    boxShadow:
                        "0 8px 22px -6px rgba(127,0,255,0.55), inset 0 1px 0 rgba(255,255,255,0.22)",
                }}
            >
                <Icon className='text-lg md:text-xl text-white' />
            </div>
            <div className='min-w-0'>
                <p className='text-client-title text-[10px] md:text-xs uppercase tracking-[0.18em] font-semibold'>
                    Section {num}
                </p>
                <h2 className='text-client-font font-bold text-lg md:text-2xl tracking-tight leading-tight'>
                    {title}
                </h2>
            </div>
        </div>
        <div className='relative text-client-font/75 text-sm md:text-base leading-relaxed space-y-4'>
            {children}
        </div>
    </motion.section>
);

const Bullet = ({ children }) => (
    <li className='flex items-start gap-2.5'>
        <span
            className='mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0'
            style={{
                background: "linear-gradient(135deg, #7F00FF, #FCFA02)",
                boxShadow: "0 0 6px rgba(127,0,255,0.5)",
            }}
        />
        <span>{children}</span>
    </li>
);

const CheckCard = ({ children }) => (
    <div
        className='flex items-start gap-2.5 rounded-xl p-3 md:p-4'
        style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
        }}
    >
        <div
            className='mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0'
            style={{
                background: "linear-gradient(135deg, #FCFA02, #FF8AFF)",
            }}
        >
            <span className='text-[10px] font-bold text-client-back'>✓</span>
        </div>
        <span className='text-client-font/80 text-sm'>{children}</span>
    </div>
);

const Note = ({ children }) => (
    <div
        className='rounded-xl p-4'
        style={{
            background:
                "linear-gradient(135deg, rgba(127,0,255,0.12), rgba(252,250,2,0.04))",
            border: "1px solid rgba(127,0,255,0.25)",
        }}
    >
        <p className='text-client-font/85 text-sm leading-relaxed'>{children}</p>
    </div>
);

const ServiceCard = ({ title, children }) => (
    <motion.div
        whileHover={{ y: -3, scale: 1.01 }}
        transition={{ type: "spring", stiffness: 300, damping: 22 }}
        className='rounded-2xl p-4 md:p-5'
        style={{
            background:
                "linear-gradient(135deg, rgba(127,0,255,0.16), rgba(127,0,255,0.04))",
            border: "1px solid rgba(127,0,255,0.3)",
            boxShadow:
                "0 8px 22px -8px rgba(127,0,255,0.35), inset 0 1px 0 rgba(255,255,255,0.1)",
        }}
    >
        <h3
            className='font-bold text-base md:text-lg mb-1.5 inline-flex items-center gap-2'
            style={{ color: "#FCFA02" }}
        >
            <span
                className='w-1.5 h-1.5 rounded-full inline-block'
                style={{ background: "#FCFA02" }}
            />
            {title}
        </h3>
        <p className='text-client-font/75 text-sm leading-relaxed'>{children}</p>
    </motion.div>
);

const ExternalLink = ({ href, children, internal }) => (
    <a
        href={href}
        target={internal ? undefined : "_blank"}
        rel={internal ? undefined : "noopener noreferrer"}
        className='font-semibold underline decoration-2 underline-offset-2 transition-colors'
        style={{ color: "#FCFA02" }}
    >
        {children}
    </a>
);

const Terms = () => {
    const [activeSection, setActiveSection] = useState("accept");

    useEffect(() => {
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) setActiveSection(e.target.id);
                });
            },
            { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
        );
        SECTIONS.forEach((s) => {
            const el = document.getElementById(s.id);
            if (el) obs.observe(el);
        });
        return () => obs.disconnect();
    }, []);

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div className='min-h-screen dm-sans client-home-bg'>
            <ClientNavbar />
            <FixedButton />

            {/* HERO */}
            <section className='relative w-full overflow-hidden'>
                <motion.div
                    aria-hidden
                    animate={{ x: [0, 40, -20, 0], y: [0, -30, 30, 0] }}
                    transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
                    className='absolute -top-10 -left-20 w-80 h-80 rounded-full opacity-35 blur-3xl pointer-events-none'
                    style={{
                        background:
                            "radial-gradient(circle, #7F00FF 0%, transparent 70%)",
                    }}
                />
                <motion.div
                    aria-hidden
                    animate={{ x: [0, -40, 30, 0], y: [0, 30, -40, 0] }}
                    transition={{ duration: 24, repeat: Infinity, ease: "easeInOut" }}
                    className='absolute top-10 -right-20 w-96 h-96 rounded-full opacity-25 blur-3xl pointer-events-none'
                    style={{
                        background:
                            "radial-gradient(circle, #FCFA02 0%, transparent 70%)",
                    }}
                />
                <div className='relative mx-auto max-w-5xl px-4 sm:px-6 md:px-8 pt-8 md:pt-14 pb-6 md:pb-10 text-center'>
                    {/* DSPY1 — top-of-page display ad */}
                    <ClientAdSense
                        slot='9729451986'
                        containerClassName='w-full mb-6 md:mb-10 text-left'
                    />

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className='inline-flex items-center gap-2 ios-glass px-3 py-1.5 rounded-full text-[10px] md:text-xs text-client-font/85 mb-4'>
                            <FiFileText style={{ color: "#FCFA02" }} />
                            <span className='tracking-wider uppercase'>Legal</span>
                        </div>
                        <h1 className='text-4xl md:text-6xl font-bold text-client-font tracking-tight leading-tight'>
                            Terms &{" "}
                            <span
                                style={{
                                    background:
                                        "linear-gradient(90deg, #FCFA02, #FF8AFF, #7F00FF)",
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    backgroundClip: "text",
                                }}
                            >
                                Conditions
                            </span>
                        </h1>
                        <p className='text-client-font/60 text-sm md:text-lg mt-4 max-w-2xl mx-auto leading-relaxed'>
                            The rules and guidelines for using PicPromptify — clearly explained,
                            so you know exactly where you stand.
                        </p>
                        <div
                            className='inline-flex items-center gap-2 mt-5 px-3 py-1.5 rounded-full text-[10px] md:text-xs text-client-font/75'
                            style={{
                                background: "rgba(255,255,255,0.05)",
                                border: "1px solid rgba(255,255,255,0.1)",
                            }}
                        >
                            <FiRefreshCw className='text-[11px]' />
                            Last updated: January 2026
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* CONTENT */}
            <section className='relative w-full px-4 sm:px-6 md:px-8 pb-12 md:pb-20'>
                <div className='mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10 mt-6 md:mt-10'>
                    {/* Sidebar TOC */}
                    <aside className='lg:col-span-4 xl:col-span-3 lg:sticky lg:top-24 lg:self-start'>
                        <div
                            className='rounded-3xl p-4 md:p-5'
                            style={{
                                background: "rgba(255,255,255,0.04)",
                                backdropFilter: "blur(18px) saturate(180%)",
                                WebkitBackdropFilter: "blur(18px) saturate(180%)",
                                border: "1px solid rgba(255,255,255,0.1)",
                                boxShadow:
                                    "0 14px 36px -16px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08)",
                            }}
                        >
                            <p className='text-client-title text-[10px] uppercase tracking-[0.2em] font-semibold mb-3'>
                                On this page
                            </p>
                            <nav className='space-y-1'>
                                {SECTIONS.map((s, i) => {
                                    const Icon = s.icon;
                                    const active = activeSection === s.id;
                                    return (
                                        <motion.button
                                            key={s.id}
                                            whileHover={{ x: 4 }}
                                            onClick={() => scrollTo(s.id)}
                                            className='relative w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-left transition-colors'
                                            style={{
                                                background: active
                                                    ? "linear-gradient(135deg, rgba(127,0,255,0.25), rgba(252,250,2,0.06))"
                                                    : "transparent",
                                                border: active
                                                    ? "1px solid rgba(127,0,255,0.4)"
                                                    : "1px solid transparent",
                                                color: active
                                                    ? "#FCFA02"
                                                    : "rgba(255,255,255,0.6)",
                                            }}
                                        >
                                            <Icon className='text-sm flex-shrink-0' />
                                            <span className='text-[12px] md:text-xs font-medium flex-1 truncate'>
                                                {i + 1}. {s.label}
                                            </span>
                                            {active && (
                                                <FiArrowRight
                                                    className='text-xs flex-shrink-0'
                                                    style={{ color: "#FCFA02" }}
                                                />
                                            )}
                                        </motion.button>
                                    );
                                })}
                            </nav>
                        </div>
                    </aside>

                    {/* Main */}
                    <div className='lg:col-span-8 xl:col-span-9 space-y-5 md:space-y-6'>
                        {/* Intro */}
                        <motion.div
                            {...fadeUp}
                            className='ios-glass rounded-3xl p-5 md:p-6'
                        >
                            <p className='text-client-font/80 text-sm md:text-base leading-relaxed'>
                                Welcome to <span className='text-client-font font-semibold'>PicPromptify</span>!
                                These Terms govern your use of our website and services. By accessing or
                                using{" "}
                                <ExternalLink href='https://picpromptify.com/'>
                                    picpromptify.com
                                </ExternalLink>
                                , you agree to comply with these Terms. If you disagree, please do not use
                                our site.
                            </p>
                        </motion.div>

                        <SectionShell id='accept' num='1' icon={FiCheckCircle} title='Acceptance of Terms'>
                            <p>
                                By using PicPromptify — an AI image prompt generator and editing tool — you
                                confirm that you are at least 13 years old and agree to be bound by these
                                Terms, our{" "}
                                <ExternalLink href='/privacy-policy' internal>
                                    Privacy Policy
                                </ExternalLink>
                                , and any applicable laws.
                            </p>
                        </SectionShell>

                        <SectionShell id='service' num='2' icon={FiInfo} title='Description of Service'>
                            <p>PicPromptify provides a free, web-based platform that allows users to:</p>
                            <ul className='space-y-2'>
                                <Bullet>Input text prompts to generate creative AI image ideas</Bullet>
                                <Bullet>
                                    Edit or refine image prompts for use with third-party AI image
                                    generators (e.g., DALL·E, Midjourney, Stable Diffusion)
                                </Bullet>
                            </ul>
                            <Note>
                                <span className='font-semibold text-client-title'>Important:</span>{" "}
                                PicPromptify does not generate, store, or host images. We only process text
                                prompts to help users craft better inputs for external AI tools. Any images
                                created using your prompts are governed by the terms of those third-party
                                platforms.
                            </Note>
                        </SectionShell>

                        <SectionShell id='responsibilities' num='3' icon={FiUserCheck} title='User Responsibilities'>
                            <p>You agree to:</p>
                            <div className='grid sm:grid-cols-2 gap-2.5'>
                                <CheckCard>
                                    Use the service only for lawful, ethical, and non-infringing purposes
                                </CheckCard>
                                <CheckCard>
                                    Not input prompts that are offensive, harmful, illegal, or violate
                                    others' rights
                                </CheckCard>
                                <CheckCard>
                                    Not scrape, reverse-engineer, or overload our systems
                                </CheckCard>
                                <CheckCard>Not use bots or automated scripts without permission</CheckCard>
                            </div>
                            <p>
                                We reserve the right to suspend or ban users who violate these rules.
                            </p>
                        </SectionShell>

                        <SectionShell id='ip' num='4' icon={FiAward} title='Intellectual Property'>
                            <p>
                                The PicPromptify website, logo, design, and code are owned by us and
                                protected by copyright laws.
                            </p>
                            <p>
                                User-generated prompts remain your property, but by submitting them, you
                                grant us a limited, non-exclusive license to process and display them
                                temporarily for service delivery.
                            </p>
                            <p>
                                AI-generated images (created via third-party tools) are not owned or
                                controlled by PicPromptify. Check the terms of the AI platform you use for
                                image rights.
                            </p>
                        </SectionShell>

                        <SectionShell id='thirdparty' num='5' icon={FiUsers} title='Third-Party Services & Ads'>
                            <p>PicPromptify integrates with:</p>
                            <div className='space-y-3'>
                                <ServiceCard title='Google AdSense'>
                                    Used to display ads and support our free service.
                                </ServiceCard>
                                <ServiceCard title='Firebase & Google Analytics'>
                                    Used to monitor performance and improve user experience.
                                </ServiceCard>
                            </div>
                            <p>
                                We are not responsible for the content, privacy practices, or availability
                                of third-party sites or AI tools linked from our platform. Ads served via
                                AdSense are managed by Google — learn more at{" "}
                                <ExternalLink href='https://support.google.com/adspolicy/'>
                                    Google's Advertising Policies
                                </ExternalLink>
                                .
                            </p>
                        </SectionShell>

                        <SectionShell id='warranty' num='6' icon={FiAlertTriangle} title='No Warranty'>
                            <p>
                                PicPromptify is provided <span className='font-semibold text-client-title'>"as is"</span> and{" "}
                                <span className='font-semibold text-client-title'>"as available."</span> We do not guarantee:
                            </p>
                            <ul className='space-y-2'>
                                <Bullet>Uninterrupted or error-free service</Bullet>
                                <Bullet>Accuracy or usefulness of generated prompts</Bullet>
                                <Bullet>Compatibility with all devices or AI platforms</Bullet>
                            </ul>
                            <p>
                                To the fullest extent permitted by law, we disclaim all warranties, express
                                or implied.
                            </p>
                        </SectionShell>

                        <SectionShell id='liability' num='7' icon={FiShieldOff} title='Limitation of Liability'>
                            <p>
                                Under no circumstances shall PicPromptify, its owners, or affiliates be
                                liable for:
                            </p>
                            <ul className='space-y-2'>
                                <Bullet>Indirect, incidental, or consequential damages</Bullet>
                                <Bullet>Loss of data, profits, or business opportunities</Bullet>
                                <Bullet>Issues arising from third-party AI tools or ad networks</Bullet>
                            </ul>
                            <Note>
                                Our total liability for any claim related to the service is limited to{" "}
                                <span className='font-semibold text-client-title'>$0 (zero USD)</span>, as
                                the service is free to use.
                            </Note>
                        </SectionShell>

                        <SectionShell id='modifications' num='8' icon={FiRefreshCw} title='Modifications to Service or Terms'>
                            <p>
                                We may update, suspend, or discontinue PicPromptify at any time. We may also
                                revise these Terms. Changes will be posted here with a new "Last Updated"
                                date. Continued use constitutes acceptance.
                            </p>
                        </SectionShell>

                        <SectionShell id='law' num='9' icon={FiGlobe} title='Governing Law'>
                            <p>
                                These Terms are governed by the laws of{" "}
                                <span className='font-semibold text-client-title'>India</span>. Any disputes
                                will be resolved in the courts of India.
                            </p>
                        </SectionShell>

                        {/* Contact (highlighted) */}
                        <motion.section
                            id='contact'
                            {...fadeUp}
                            className='relative rounded-3xl p-5 md:p-8 overflow-hidden scroll-mt-28'
                            style={{
                                background:
                                    "linear-gradient(135deg, rgba(127,0,255,0.2), rgba(252,250,2,0.06))",
                                border: "1px solid rgba(127,0,255,0.35)",
                                backdropFilter: "blur(18px) saturate(180%)",
                                WebkitBackdropFilter: "blur(18px) saturate(180%)",
                                boxShadow:
                                    "0 18px 40px -12px rgba(127,0,255,0.45), inset 0 1px 0 rgba(255,255,255,0.14)",
                            }}
                        >
                            <motion.div
                                aria-hidden
                                animate={{ rotate: 360 }}
                                transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                                className='absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-25 blur-3xl pointer-events-none'
                                style={{
                                    background:
                                        "conic-gradient(from 0deg, #7F00FF, #FF8AFF, #FCFA02, #7F00FF)",
                                }}
                            />
                            <div className='relative flex items-center gap-3 mb-5'>
                                <div
                                    className='w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0'
                                    style={{
                                        background:
                                            "linear-gradient(135deg, #FCFA02, #FF8AFF, #7F00FF)",
                                        boxShadow:
                                            "0 12px 28px -8px rgba(127,0,255,0.6)",
                                    }}
                                >
                                    <FiMail className='text-xl text-white' />
                                </div>
                                <div>
                                    <p className='text-client-title text-[10px] md:text-xs uppercase tracking-[0.18em] font-semibold'>
                                        Section 10
                                    </p>
                                    <h2 className='text-client-font font-bold text-lg md:text-2xl tracking-tight leading-tight'>
                                        Contact Us
                                    </h2>
                                </div>
                            </div>
                            <p className='relative text-client-font/80 text-sm md:text-base mb-5'>
                                Questions about these Terms? Reach out — we'll respond within 1–2 business
                                days.
                            </p>
                            <div className='relative grid sm:grid-cols-2 gap-3'>
                                <motion.a
                                    whileHover={{ y: -3, scale: 1.02 }}
                                    whileTap={{ scale: 0.97 }}
                                    href='mailto:picpromptify@gmail.com'
                                    className='flex items-center gap-3 rounded-2xl p-4'
                                    style={{
                                        background: "rgba(255,255,255,0.05)",
                                        border: "1px solid rgba(255,255,255,0.12)",
                                        backdropFilter: "blur(14px)",
                                    }}
                                >
                                    <div
                                        className='w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0'
                                        style={{
                                            background:
                                                "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                                        }}
                                    >
                                        <FiMail className='text-base text-white' />
                                    </div>
                                    <div className='min-w-0'>
                                        <p className='text-client-font/55 text-[10px] uppercase tracking-wider'>
                                            Email
                                        </p>
                                        <p className='text-client-font font-semibold text-sm truncate'>
                                            picpromptify@gmail.com
                                        </p>
                                    </div>
                                </motion.a>
                                <motion.a
                                    whileHover={{ y: -3, scale: 1.02 }}
                                    whileTap={{ scale: 0.97 }}
                                    href='https://picpromptify.com/'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='flex items-center gap-3 rounded-2xl p-4'
                                    style={{
                                        background: "rgba(255,255,255,0.05)",
                                        border: "1px solid rgba(255,255,255,0.12)",
                                        backdropFilter: "blur(14px)",
                                    }}
                                >
                                    <div
                                        className='w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0'
                                        style={{
                                            background:
                                                "linear-gradient(135deg, #FCFA02, #FF8AFF)",
                                        }}
                                    >
                                        <FiGlobe className='text-base text-white' />
                                    </div>
                                    <div className='min-w-0'>
                                        <p className='text-client-font/55 text-[10px] uppercase tracking-wider'>
                                            Website
                                        </p>
                                        <p className='text-client-font font-semibold text-sm truncate'>
                                            picpromptify.com
                                        </p>
                                    </div>
                                </motion.a>
                            </div>
                        </motion.section>

                        {/* Back to top */}
                        <div className='flex justify-center pt-2'>
                            <motion.button
                                whileHover={{ y: -3, scale: 1.04 }}
                                whileTap={{ scale: 0.96 }}
                                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                                className='ios-glass-btn inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-client-font text-xs md:text-sm font-medium'
                            >
                                <FiArrowUp />
                                Back to top
                            </motion.button>
                        </div>
                    </div>
                </div>
            </section>

            <ClientFooter />
        </div>
    );
};

export default Terms;
